import { useState, useEffect, HTMLAttributes, useCallback } from "react";
import {
  useRoleName,
  useRole,
  useContractContext,
  useContract,
} from "../ContractContext";
import {
  ContractField,
  ContractFieldWrapper,
  useContractField,
} from "../fields/ContractFieldInput";
import { FieldInput, FieldType } from "../fields/FieldInput";
import { BUYER } from "../roles";
import CardSection from "../../formatting/CardSection";
import {
  PatternValidationParameters,
  useInputValidation,
  ValidationErrorResult,
} from "../validation/InputValidationContext";
import ContractFieldsView from "../fields/ContractFieldsView";
import ROLES from "../roles";
import { useContractClient } from "../../api/ContractClient";
import {
  ContractingParty,
  ContractViewModel,
  Role,
} from "../model/ContractViewModel";
import { validateOrganizationNumber } from "../fields/OrganizationNumber";
import { CoOwnerCard } from "./CoOwnerCard";
import styled from "styled-components";
import { onDesktop } from "../../shared/layout/Breakpoints";
import ContractCheckboxField from "../fields/form-controls/ContractCheckboxField";
import { Button } from "@teamscheme/button";
import { useClientBase } from "../../api/ClientBase";
import ErrorMessage from "../../shared/error/ErrorMessage";
import {
  AdditionalFieldDescription,
  NationalIdentityNumberInputField,
  OrganizationNumberInputField,
} from "./CustomContactInfoInputFields";
import { InfoAlert } from "@teamscheme/alert";
import {
  hasFeature,
  useContractUpgrade,
} from "../contract-type/ContractTypePage";

export const EMAIL_PATTERN =
  '^(([^<>()\\[\\]\\\\.,;:\\s@"]+(\\.[^<>()\\[\\]\\\\.,;:\\s@"]+)*)|(".+"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$';

interface FieldValue {
  stringValue?: string;
}
interface Field {
  name: keyof ContractingParty;
  type?: FieldType;
  autocomplete?: string;
  required?: boolean;
  labelHint?: string;
  pattern?: PatternValidationParameters;
  format(value: string): string;
  toFieldValue?: (rawValue: string) => FieldValue;
  validate?: (value: string | null) => ValidationErrorResult | null;
}

const CO_OWNER_FIELDS: Field[] = [
  {
    name: "phone",
    type: "phone",
    format: (value) => value || "-",
  },
  {
    name: "firstName",
    format: (value) => value || "-",
  },
  {
    name: "lastName",
    format: (value) => value || "-",
  },
  {
    name: "email",
    type: "email",
    format: (value) => value || "-",
  },
];

function CONTACT_INFO_FIELDS(
  contract: ContractViewModel,
  hasOwnershipTransferFeature: boolean
): Field[] {
  const source = [
    {
      name: "phone",
      type: "phone",
      //autocomplete: "tel",
      required: true,
      pattern: {
        match: "^((\\+|00)47\\s?)?\\d{3}\\s?\\d{2}\\s?\\d{3}$",
        error: "Fyll inn et gyldig norsk telefonnummer",
      },
      format: (value) => value || "-",
    },
    {
      name: "firstName",
      //autocomplete: "given-name",
      required: true,
      format: (value) => value || "-",
    },
    {
      name: "lastName",
      //autocomplete: "family-name",
      required: true,
      format: (value) => value || "-",
    },
    {
      name: "address",
      //autocomplete: "street-address",
      required: true,
      format: (value) => value || "-",
    },
    {
      name: "postalCode",
      type: "intString",
      //autocomplete: "postal-code",
      required: true,
      format: (value) => value || "-",
    },
    {
      name: "city",
      //autocomplete: "address-level2",
      required: true,
      format: (value) => value || "-",
    },
    {
      name: "email",
      type: "email",
      //autocomplete: "email",
      required: true,
      format: (value) => value || "-",
      pattern: {
        match: EMAIL_PATTERN,
        error: "Fyll inn en gyldig e-postadresse",
      },
    },
    contract.ownershipTransfer?.featureEnabled
      ? {
          name: "nationalIdentityNumber",
          type: "tel",
          autocomplete: "off",
          required: hasOwnershipTransferFeature,
          format: (value) => value || "-",
        }
      : null,
    {
      name: "accountNumber",
      type: "account",
      autocomplete: "off",
      toFieldValue: (stringValue) => ({ stringValue }),
      format: (value) => value || "-",
    },
  ] as (Field | null)[];

  return source.filter(notEmpty);
}

function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}

interface AutofillContactInfoResult {
  contract: ContractViewModel;
  filled: boolean;
}

interface AutofillContactInfoRequest {
  phone: string;
}

class ContactInfoNotFoundError extends Error {
  noMatch = true;
  constructor() {
    super("No match");
  }
}

export function useAutofillContactInfo(phone: string | null) {
  const client = useContractClient();
  const contract = useContract();
  const { onChange } = useContractContext();

  const base = useClientBase(
    useCallback(
      function (request: AutofillContactInfoRequest) {
        if (!phone) return null;
        const promise = client.post<AutofillContactInfoResult>(
          `contract/${contract.id}/autofill/contactinfo`,
          request
        );

        return promise.then((x) => {
          if (x.filled) onChange(x.contract);
          else throw new ContactInfoNotFoundError();

          return x;
        });
      },
      [phone, client, contract.id, onChange]
    )
  );

  return base;
}

const CompanyInfo = styled(InfoAlert)`
  margin-left: 1em;
  margin-right: 1em;
  margin-top: -1em;
`;

const PhoneNumberFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  ${onDesktop`flex-direction: row;`}
`;

const AutofillButton = styled(Button)`
  padding: 0 1em;
  flex: 0;
  margin: ${({ theme }) => theme.spacing.space8} 0 0;
  ${({ theme }) =>
    onDesktop`
      margin:  0 0 0 ${theme.spacing.space16}
    `}
`;

const AutoFilledAlert = styled(InfoAlert)`
  margin-bottom: ${({ theme }) => theme.spacing.space20};
`;

const ContactInfoForm = styled(function ({
  section,
  className,
  ...props
}: {
  section: "buyer" | "seller";
} & HTMLAttributes<HTMLDivElement>) {
  const { saveField } = useContractContext();
  const contract = useContract();
  const products = useContractUpgrade();

  const party = contract[section];
  const values = (party || {}) as ContractingParty;
  const storedPhoneValue = values.phone;
  const [phoneValue, setPhoneValue] = useState(storedPhoneValue || null);
  const role = useRole();
  const autofill = useAutofillContactInfo(phoneValue);

  useEffect(() => {
    setPhoneValue(storedPhoneValue || null);
  }, [storedPhoneValue]);

  const isOrganization = party?.isOrganization;
  const storedOrganizationNumberValue = values.organizationNumber;
  const [organizationNumberValue, setOrganizationNumberValue] = useState(
    storedOrganizationNumberValue || null
  );

  useEffect(() => {
    setOrganizationNumberValue(storedOrganizationNumberValue || null);
  }, [storedOrganizationNumberValue]);

  const {
    validate: validateOrganizationNumberRequired,
    error: organizationNumberError,
  } = useInputValidation(section, "organizationNumber", {
    required: true,
    callback: function (value: string | null) {
      if (!value) return null;

      const x = validateOrganizationNumber(value);

      if (x.valid) return null;

      return {
        patternError: "Dette ser ikke ut som et gyldig organisasjonsnummer",
      };
    },
  });

  const contactInfoFields = CONTACT_INFO_FIELDS(
    contract,
    products.result
      ? hasFeature(contract, products.result, (x) => x.ownershipTransfer)
      : false
  );

  const phoneInput = contactInfoFields.filter(
    ({ name }) => name === "phone"
  )[0];
  const nationalIdentityNumberInput = contactInfoFields.filter(
    ({ name }) => name === "nationalIdentityNumber"
  )[0];
  const inputs = contactInfoFields.filter(
    ({ name }) =>
      name !== phoneInput.name && name !== nationalIdentityNumberInput.name
  );

  const { validate: validatePhone, error: phoneError } = useInputValidation(
    section,
    phoneInput.name,
    {
      required: phoneInput.required,
      pattern: phoneInput.pattern,
    }
  );

  const nationalIdentityNumberValue = useContractField<string>({
    section,
    name: nationalIdentityNumberInput.name,
    type: nationalIdentityNumberInput.type ?? "tel",
    validation: {
      pattern: nationalIdentityNumberInput.pattern,
      required: nationalIdentityNumberInput.required,
      callback: nationalIdentityNumberInput.validate,
    },
  });

  const isOrganizationLabel =
    role === Role.Seller
      ? "Bilen selges på vegne av firma"
      : "Bilen kjøpes på vegne av firma";

  return (
    <div
      className={["ContactInfoForm", className].filter((c) => c).join(" ")}
      {...props}
    >
      <ContractCheckboxField
        section={section}
        name="isOrganization"
        label={isOrganizationLabel}
      />
      {isOrganization ? (
        <>
          <CompanyInfo>
            Kjøpekontrakten må signeres av firmaets prokurist, det vil si de(n)
            personen(e) som har fullmakt til å signere på vegne av firma.
          </CompanyInfo>
          <ContractFieldWrapper
            section={section}
            name="organizationNumber"
            type="text"
            required
            hasError={!!organizationNumberError}
          >
            <OrganizationNumberInputField
              section={section}
              value={{
                current: organizationNumberValue,
                onChange: (value: string) => setOrganizationNumberValue(value),
                onSave: async (value: string) => {
                  if (value === storedPhoneValue) return;

                  validateOrganizationNumberRequired(value);

                  await saveField(
                    section,
                    "organizationNumber",
                    value,
                    value
                      ? {
                          stringValue: value,
                        }
                      : null
                  );
                },
              }}
            />
          </ContractFieldWrapper>
          <ContractField
            section={section}
            name="organizationName"
            type="text"
          />
        </>
      ) : null}
      <ContractFieldWrapper
        key={phoneInput.name}
        section={section}
        name={phoneInput.name}
        type={"phone"}
      >
        <PhoneNumberFieldWrapper>
          <FieldInput
            section={section}
            name={phoneInput.name}
            type={"phone"}
            required={phoneInput.required}
            inputProps={{
              autoComplete: phoneInput.autocomplete,
              disabled: autofill.isLoading,
              onKeyDown: ({ key }) => {
                if (key === "Enter") {
                  if (!!phoneValue) autofill.fetch({ phone: phoneValue });
                }
              },
            }}
            hasError={!!phoneError || !!autofill.error}
            value={{
              current: phoneValue,
              onChange: (value: string) => setPhoneValue(value),
              onSave: async (value: string) => {
                if (value === storedPhoneValue) return;

                if (value?.substring(0, 3) === "+47") {
                  validatePhone(value);
                }

                await saveField(
                  section,
                  phoneInput.name,
                  value,
                  value
                    ? {
                        stringValue: value,
                      }
                    : null
                );
              },
            }}
          />
          <AutofillButton
            disabled={phoneValue === null}
            isLoading={autofill.isLoading}
            variant="secondary"
            type="button"
            onClick={async (
              e: React.MouseEvent<HTMLButtonElement, MouseEvent>
            ) => {
              e.preventDefault();
              if (!!phoneValue) autofill.fetch({ phone: phoneValue });
            }}
          >
            Hent opplysninger
          </AutofillButton>
        </PhoneNumberFieldWrapper>
        {autofill.error ? (
          <ErrorMessage>
            {autofill.error instanceof ContactInfoNotFoundError
              ? "Fikk ingen treff på dette telefonnummeret, vennligst fyll ut opplysningene manuelt"
              : "En feil har oppstått. Prøv igjen."}
          </ErrorMessage>
        ) : null}
      </ContractFieldWrapper>
      {inputs
        .filter((field) => {
          if (field.name === "accountNumber" && role === ROLES.BUYER) {
            return false;
          }
          return true;
        })
        .map((field) => (
          <ContractField
            key={field.name}
            section={section}
            name={field.name}
            type={field.type || "text"}
            required={field.required}
            inputProps={{
              disabled: autofill.isLoading,
              autoComplete: field.autocomplete,
            }}
            validation={{
              pattern: field.pattern,
              required: field.required,
              callback: field.validate,
            }}
            labelHint={field.labelHint}
          />
        ))}
      <NationalIdentityNumberInputField
        section={section}
        value={nationalIdentityNumberValue}
        inputProps={{
          autoComplete: nationalIdentityNumberInput.autocomplete,
          disabled: autofill.isLoading,
        }}
        labelHint={nationalIdentityNumberInput.labelHint}
      />
      <AdditionalFieldDescription>
        For BankID, eierskifte og betaling av kjøretøyet med Vipps.
      </AdditionalFieldDescription>
    </div>
  );
})`
  display: flex;
  flex-wrap: wrap;
  margin: 0 -${(props) => props.theme.spacing.space8};

  .FormGroup {
    width: 100%;
    padding: 0 ${(props) => props.theme.spacing.space8};
    box-sizing: border-box;
  }

  ${onDesktop`
    .FormGroup.ContractField-city,
    .FormGroup.ContractField-postalCode {
      width: 50%;
    }

    .FormGroup.ContractField-firstName,
    .FormGroup.ContractField-lastName {
      width: 50%;
    }
  `}
`;

const CoOwnerView = styled.div`
  h2 {
    text-align: center;
  }
  margin-top: 1em;
`;

export default function ContactInfoSection() {
  const contract = useContract();
  const role = useRole();
  const section = role === BUYER ? "buyer" : "seller";
  const coOwnerSection = role === BUYER ? "buyerCoOwner" : "sellerCoOwner";
  const contractingParty = contract[section] || ({} as ContractingParty);
  const coOwner = contract[coOwnerSection];
  const { autoFilled } = contractingParty;
  const { self } = useRoleName();

  return (
    <CardSection
      title="Dine opplysninger"
      ingress={`Fyll ut opplysninger om deg som ${self}.`}
    >
      {contract.signature ? (
        <>
          <ContractFieldsView
            section={section}
            fields={CONTACT_INFO_FIELDS(contract, false)}
          />
          {coOwner?.included ? (
            <CoOwnerView>
              <h2>Medeiers opplysninger</h2>
              <ContractFieldsView
                section={coOwnerSection}
                fields={CO_OWNER_FIELDS}
              />
            </CoOwnerView>
          ) : null}
        </>
      ) : (
        <>
          {autoFilled ? (
            <AutoFilledAlert>
              Vi har fylt inn informasjon basert på ditt mobilnummer. Sjekk at
              den stemmer og fyll inn e-postadresse.
            </AutoFilledAlert>
          ) : null}
          <ContactInfoForm section={section} />
          <CoOwnerCard />
        </>
      )}
    </CardSection>
  );
}
